import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Typography,
  Divider,
  Grid,
  Card,
  CardContent,
  CardActions,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';

import {
  Save as SaveIcon,
  Logout as LogoutIcon,
  QrCode as QrCodeIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useModal, toast } from '../common/components/premium';
import PremiumInput from '../common/components/premium/PremiumInput';

const WhatsappPage = () => {
  const tokens = usePremiumTokens();
  const { confirm } = useModal();

  // Estados principais
  const [apiType, setApiType] = useState('fzapw');
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [endpointType, setEndpointType] = useState('text');

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  // Estados de teste
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Teste de conexão WhatsApp');

  // Estados de QR Code
  const [qrCodeData, setQrCodeData] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrError, setQrError] = useState(null);

  // Carregar configuração ao montar
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const sessionResponse = await fetch('/api/session');
      if (!sessionResponse.ok) throw new Error('Erro ao carregar sessão');

      const session = await sessionResponse.json();

      const userResponse = await fetch(`/api/users/${session.id}`);
      if (!userResponse.ok) throw new Error('Erro ao carregar usuário');

      const user = await userResponse.json();
      const formData = user.attributes?.form || {};

      setApiType(formData.whatsappApiType || 'fzapw');
      setUrl(formData.whatsappUrl || '');
      setApiKey(formData.whatsappApiKey || '');
      setSessionId(formData.whatsappSessionId || '');
      setEndpointType(formData.whatsappEndpoint || 'text');

      // Verificar status se configurado
      if (formData.whatsappUrl && formData.whatsappApiKey) {
        checkStatus(
          formData.whatsappApiType || 'fzapw',
          formData.whatsappUrl,
          formData.whatsappApiKey,
          formData.whatsappSessionId
        );
      }
    } catch (error) {
      console.error('Error loading config:', error);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);

      const sessionResponse = await fetch('/api/session');
      if (!sessionResponse.ok) throw new Error('Erro ao carregar sessão');

      const session = await sessionResponse.json();
      const userId = session.id;

      const userResponse = await fetch(`/api/users/${userId}`);
      if (!userResponse.ok) throw new Error('Erro ao carregar usuário');

      const user = await userResponse.json();

      const updatedUser = {
        ...user,
        attributes: {
          ...user.attributes,
          form: {
            ...(user.attributes?.form || {}),
            whatsappApiType: apiType,
            whatsappUrl: url,
            whatsappApiKey: apiKey,
            whatsappSessionId: sessionId,
            whatsappEndpoint: endpointType,
          },
        },
      };

      const saveResponse = await fetch(`/api/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedUser),
      });

      if (!saveResponse.ok) {
        const errorText = await saveResponse.text();
        throw new Error(`Erro ao salvar: ${errorText}`);
      }

      toast.success('Configuração salva com sucesso!');
      checkStatus(apiType, url, apiKey, sessionId);
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const checkStatus = useCallback(async (type, baseUrl, key, session) => {
    try {
      setLoading(true);

      const currentApiType = type || apiType;
      const response = await fetch('/api/whatsapp/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiType: currentApiType,
          url: baseUrl || url,
          apiKey: key || apiKey,
          sessionId: session || sessionId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({
          connected: data.connected || false,
          loggedIn: data.loggedIn !== undefined ? data.loggedIn : true, // Fallback para Evolution
          lastCheck: new Date().toISOString(),
        });
      } else {
        setStatus({
          connected: false,
          loggedIn: false,
          lastCheck: new Date().toISOString(),
          error: data.error || 'Erro ao verificar status',
        });
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setStatus({
        connected: false,
        loggedIn: false,
        lastCheck: new Date().toISOString(),
        error: error.message,
      });
    } finally {
      setLoading(false);
    }
  }, [apiType, url, apiKey, sessionId]);

  const handleLogout = async () => {
    const confirmed = await confirm({
      title: 'Desconectar WhatsApp',
      message: 'Deseja realmente desconectar a instância? Você precisará escanear o QR Code novamente para reconectar.',
      confirmText: 'Desconectar',
      confirmVariant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    try {
      setLoading(true);
      toast.loading('Desconectando...');

      const response = await fetch('/api/whatsapp/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiType, url, apiKey, sessionId }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Instância desconectada com sucesso!');
        setStatus({
          connected: false,
          loggedIn: false,
          lastCheck: new Date().toISOString(),
        });
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Erro ao desconectar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    try {
      setLoading(true);
      setQrCodeData(null);
      setQrError(null);

      // Primeiro verificar se já está conectado
      const statusResponse = await fetch('/api/whatsapp/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiType, url, apiKey, sessionId }),
      });

      const statusData = await statusResponse.json();

      if (statusData.success && statusData.connected) {
        setQrError('Instância já está conectada! Não é necessário escanear QR Code.');
        setShowQRModal(true);
        setLoading(false);
        return;
      }

      // Se não conectado, gerar QR Code
      const response = await fetch('/api/whatsapp/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiType, url, apiKey, sessionId }),
      });

      const data = await response.json();

      if (data.success && data.qrcode) {
        const qrImage = data.qrcode.startsWith('data:')
          ? data.qrcode
          : `data:image/png;base64,${data.qrcode}`;

        setQrCodeData(qrImage);
        setShowQRModal(true);
      } else {
        setQrError(data.error || 'QR Code não retornado pela API');
        setShowQRModal(true);
      }
    } catch (error) {
      console.error('Error generating QR:', error);
      setQrError('Erro ao gerar QR Code: ' + error.message);
      setShowQRModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleTestSend = async () => {
    if (!testPhone || !testMessage) {
      toast.warning('Preencha o número e a mensagem!');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Enviando mensagem de teste...');

      const response = await fetch('/api/whatsapp/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiType,
          url,
          apiKey,
          sessionId,
          phone: testPhone,
          message: testMessage,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Mensagem enviada com sucesso para ${testPhone}!`);
      } else {
        throw new Error(data.error || 'Erro desconhecido');
      }
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Erro ao enviar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = () => {
    if (!status) {
      return (
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: tokens.border.default }} />
            <Typography variant="body2" color="text.secondary">
              Status desconhecido
            </Typography>
          </Box>
        </Box>
      );
    }

    // Determinar cor, mensagem e se deve mostrar hint do QR
    let statusColor = tokens.semantic.error;
    let statusText = 'Desconectado';
    let showQrHint = false;

    if (status.connected) {
      // Wuzapi/Evolution Go: verificar se está aguardando autenticação via QR
      if ((apiType === 'fzapw' || apiType === 'fzapg') && status.loggedIn === false) {
        statusColor = tokens.semantic.warning;
        statusText = 'Conectado (aguardando autenticação)';
        showQrHint = true;
      } else {
        statusColor = tokens.semantic.success;
        statusText = 'Conectado';
      }
    }

    return (
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: statusColor }} />
          <Typography variant="body2" color="text.secondary">
            {statusText}
          </Typography>
        </Box>

        {showQrHint && (
          <Typography
            variant="caption"
            sx={{
              color: tokens.semantic.warning,
              fontWeight: 500,
              ml: 2.5,
            }}
          >
            Clique em "Gerar QR Code" para autenticar
          </Typography>
        )}

        {status.lastCheck && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2.5 }}>
            Última verificação: {new Date(status.lastCheck).toLocaleString('pt-BR')}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">WhatsApp Configuration</Typography>
      </Box>

      {/* Layout 2 Colunas */}
      <Grid container spacing={3}>
        {/* COLUNA ESQUERDA - Formulário */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.subtle}`,
              backgroundColor: tokens.surface.card,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configurações da API
              </Typography>

              {/* Tipo de API */}
              <FormControl fullWidth margin="normal" size="small" className="premium-input">
                <InputLabel>Tipo de API</InputLabel>
                <Select
                  value={apiType}
                  onChange={(e) => setApiType(e.target.value)}
                  label="Tipo de API"
                >
                  <MenuItem value="fzapw">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: tokens.semantic.success,
                        }}
                      />
                      FZAPW (Wuzapi)
                    </Box>
                  </MenuItem>
                  <MenuItem value="fzape">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: tokens.accent.primary,
                        }}
                      />
                      FZAPE (Evolution - Legado)
                    </Box>
                  </MenuItem>
                  <MenuItem value="fzapg">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          bgcolor: tokens.semantic.warning,
                        }}
                      />
                      FZAPG (Evolution Go)
                    </Box>
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {apiType === 'fzapw' && 'Wuzapi - Autenticação via token'}
                  {apiType === 'fzape' && 'Evolution (legado) - Autenticação via apikey global'}
                  {apiType === 'fzapg' && 'Evolution Go - Autenticação via token da instância (header apikey)'}
                </FormHelperText>
              </FormControl>

              {/* URL */}
              <PremiumInput
                label={{
                  fzapw: 'URL Wuzapi',
                  fzape: 'URL Evolution',
                  fzapg: 'URL Evolution Go',
                }[apiType]}
                placeholder={
                  apiType === 'fzapw'
                    ? 'https://api-falewhats.2ecomp.com'
                    : 'https://falewhats.2ecomp.com'
                }
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* API Key */}
              <PremiumInput
                label={{
                  fzapw: 'Token (Wuzapi)',
                  fzape: 'API Key (Evolution)',
                  fzapg: 'Token da Instância (Evolution Go)',
                }[apiType]}
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* Session ID */}
              <PremiumInput
                label={{
                  fzapw: 'Session ID',
                  fzape: 'Instance Name',
                  fzapg: 'Nome da Instância (referência)',
                }[apiType]}
                placeholder={apiType === 'fzapw' ? '2ecomp' : 'ultron_alertas'}
                helperText={apiType === 'fzapg'
                  ? 'Opcional - o token já identifica a instância na Evolution Go'
                  : undefined}
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                sx={{ mt: 2 }}
              />

              {/* Endpoint Type */}
              <FormControl fullWidth margin="normal" size="small" className="premium-input">
                <InputLabel>Tipo de Mensagem</InputLabel>
                <Select
                  value={endpointType}
                  onChange={(e) => setEndpointType(e.target.value)}
                  label="Tipo de Mensagem"
                >
                  <MenuItem value="text">Text Message</MenuItem>
                  <MenuItem value="location">Location Message</MenuItem>
                </Select>
              </FormControl>

              {/* Card Informativo */}
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: tokens.radius.md,
                  bgcolor: apiType === 'fzapw' ? tokens.accent.subtle : tokens.surface.elevated,
                  border: `1px solid ${apiType === 'fzapw' ? tokens.semantic.success : tokens.accent.primary}`,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {{
                    fzapw: 'Wuzapi (FZAPW)',
                    fzape: 'Evolution - Legado (FZAPE)',
                    fzapg: 'Evolution Go (FZAPG)',
                  }[apiType]}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {apiType === 'fzapw' && (
                    <>
                      <strong>Autenticação:</strong> Header "token"<br />
                      <strong>Endpoints:</strong><br />
                      • POST /chat/send/text<br />
                      • POST /chat/send/location<br />
                      <strong>Formato:</strong> {'{Phone, Body, check}'}
                    </>
                  )}
                  {apiType === 'fzape' && (
                    <>
                      <strong>Autenticação:</strong> Header "apikey"<br />
                      <strong>Endpoints:</strong><br />
                      • POST /message/sendText/{'{instance}'}<br />
                      • POST /message/sendLocation/{'{instance}'}<br />
                      <strong>Formato:</strong> {'{number, text, delay}'}
                    </>
                  )}
                  {apiType === 'fzapg' && (
                    <>
                      <strong>Autenticação:</strong> Header "apikey" (token da instância)<br />
                      <strong>Endpoints:</strong><br />
                      • POST /send/text<br />
                      • POST /send/location<br />
                      • GET /instance/qr | /instance/status<br />
                      <strong>Formato:</strong> {'{number, text, delay}'}
                    </>
                  )}
                </Typography>
              </Paper>
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={saveConfig}
                disabled={saving || !url || !apiKey || (apiType !== 'fzapg' && !sessionId)}
                size="large"
                sx={{
                  borderRadius: tokens.radius.md,
                  backgroundColor: tokens.accent.primary,
                  '&:hover': { backgroundColor: tokens.accent.hover },
                }}
              >
                {saving ? 'Salvando...' : 'Salvar Configuração'}
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* COLUNA DIREITA - Status e Testes */}
        <Grid size={{ xs: 12, md: 5 }}>
          {/* Card Status */}
          <Card
            elevation={0}
            sx={{
              mb: 2,
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.subtle}`,
              backgroundColor: tokens.surface.card,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status da Conexão
              </Typography>

              <Box py={2}>
                {renderStatus()}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" flexDirection="column" gap={1}>
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  onClick={() => checkStatus()}
                  disabled={loading || !url || !apiKey}
                  fullWidth
                  sx={{ borderRadius: tokens.radius.md }}
                >
                  Verificar Status
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  disabled={loading || !url || !apiKey}
                  fullWidth
                  sx={{ borderRadius: tokens.radius.md }}
                >
                  Desconectar
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<QrCodeIcon />}
                  onClick={handleGenerateQR}
                  disabled={loading || !url || !apiKey || (apiType !== 'fzapg' && !sessionId)}
                  fullWidth
                  sx={{ borderRadius: tokens.radius.md }}
                >
                  Gerar QR Code
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Card Testar Envio */}
          <Card
            elevation={0}
            sx={{
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.subtle}`,
              backgroundColor: tokens.surface.card,
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Testar Envio
              </Typography>

              <PremiumInput
                label="WhatsApp"
                placeholder="5511999999999"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                helperText="Número com DDI (ex: 5511999999999)"
                sx={{ mt: 2 }}
              />

              <PremiumInput
                label="Mensagem"
                multiline
                rows={3}
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Button
                variant="contained"
                color="info"
                startIcon={<SendIcon />}
                onClick={handleTestSend}
                disabled={loading || !url || !apiKey || !testPhone || !testMessage}
                fullWidth
                sx={{
                  mt: 2,
                  borderRadius: tokens.radius.md,
                }}
              >
                Enviar Teste
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Modal QR Code */}
      <Dialog
        open={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          setQrError(null);
          checkStatus();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: tokens.radius.lg,
            backgroundColor: tokens.surface.card,
          },
        }}
      >
        <DialogTitle>
          QR Code - {apiType.toUpperCase()}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            py={2}
          >
            {qrError ? (
              <Alert severity="warning" sx={{ width: '100%' }}>
                {qrError}
              </Alert>
            ) : qrCodeData ? (
              <>
                <img
                  src={qrCodeData}
                  alt="QR Code"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: `2px solid ${tokens.border.default}`,
                    borderRadius: tokens.radius.md,
                    padding: '10px',
                    backgroundColor: 'white',
                  }}
                />
                <Alert severity="info" sx={{ width: '100%' }}>
                  Escaneie este QR Code com o WhatsApp para conectar
                </Alert>
              </>
            ) : (
              <CircularProgress />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowQRModal(false);
              setQrError(null);
              checkStatus();
            }}
            sx={{ borderRadius: tokens.radius.md }}
          >
            Fechar
          </Button>
          {!qrError && (
            <Button
              onClick={handleGenerateQR}
              color="primary"
              variant="contained"
              disabled={loading}
              sx={{
                borderRadius: tokens.radius.md,
                backgroundColor: tokens.accent.primary,
                '&:hover': { backgroundColor: tokens.accent.hover },
              }}
            >
              Atualizar QR
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsappPage;
